import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  FaUsers, 
  FaMoneyBillWave, 
  FaUser, 
  FaUserShield,
  FaBuilding,
  FaChartLine,
  FaWallet
} from "react-icons/fa";
import axios from "axios";
import { getAdminOrganizations, fetchOrgUsers } from "../../services/api";
import "../../../styles/dashboard/SuperAdmin/SuperAdminHome.css";

const API_URL = process.env.REACT_APP_API_URL;
const getToken = () => sessionStorage.getItem("token") || localStorage.getItem("token");

/**
 * Admin landing page with statistics and quick-access cards
 */
export default function AdminHome() {
  const [stats, setStats] = useState({
    subAdmins: 0,
    organizations: 0,
    users: 0,
    loading: true
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [dashboardRes, subAdminsRes, orgsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/dashboard`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        }),
        axios.get(`${API_URL}/admin/sub-admins`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        }),
        getAdminOrganizations("", 1, 100, "all"),
        fetchOrgUsers('admin')
      ]);

      // Extract data - handle different response structures  
      const dashboardData = dashboardRes.data || {};
      let subAdminsData = subAdminsRes.data || [];
      let orgsData = orgsRes.success ? orgsRes.data : [];
      let usersData = usersRes.success ? usersRes.data : [];

      // Handle if data is nested
      if (subAdminsData && typeof subAdminsData === 'object' && !Array.isArray(subAdminsData)) {
        subAdminsData = subAdminsData.subAdmins || subAdminsData.data || [];
      }
      if (orgsData && typeof orgsData === 'object' && !Array.isArray(orgsData)) {
        orgsData = orgsData.organizations || orgsData.data || [];
      }
      if (usersData && typeof usersData === 'object' && !Array.isArray(usersData)) {
        usersData = usersData.users || usersData.data || [];
      }

      const subAdminsList = Array.isArray(subAdminsData) ? subAdminsData : [];
      const subAdminsCount = subAdminsList.length;

      console.log('Admin Stats data:', { 
        dashboard: dashboardData,
        subAdminsCount, 
        orgs: orgsData.length, 
        users: usersData.length 
      });

      setStats({
        subAdmins: subAdminsCount,
        organizations: Array.isArray(orgsData) ? orgsData.length : (dashboardData.orgs || 0),
        users: Array.isArray(usersData) ? usersData.length : (dashboardData.users || 0),
        loading: false
      });
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      setStats(prev => ({ ...prev, loading: false }));
    }
  };

  const statCards = [
    {
      title: "Coordinate Agents",
      count: stats.subAdmins,
      icon: FaUsers,
      gradient: "stat-subadmin",
      link: "/admin/sub-admins"
    },
    {
      title: "Organizations",
      count: stats.organizations,
      icon: FaBuilding,
      gradient: "stat-org",
      link: "/admin/organizations"
    },
    {
      title: "Total Users",
      count: stats.users,
      icon: FaUser,
      gradient: "stat-user",
      link: "/admin/users"
    }
  ];
  
  const quickActions = [
    {
      title: "Organizations",
      description: "Manage organizations and agreements",
      link: "/admin/organizations",
      Icon: FaBuilding,
      gradient: "action-org",
    },
    {
      title: "Users",
      description: "Monitor and manage user accounts",
      link: "/admin/users",
      Icon: FaUser,
      gradient: "action-user",
    },
    {
      title: "Transactions",
      description: "View payment and transaction history",
      link: "/admin/transaction-history",
      Icon: FaMoneyBillWave,
      gradient: "action-transaction",
    },
    {
      title: "Credit Reports",
      description: "Manage credit report settings",
      link: "/admin/credit-reports",
      Icon: FaChartLine,
      gradient: "action-credit",
    },
    {
      title: "Bank Acts",
      description: "Manage bank act configurations",
      link: "/admin/bank-act",
      Icon: FaWallet,
      gradient: "action-bank",
    },
    {
      title: "Support Tickets",
      description: "Handle support requests",
      link: "/admin/support-tickets",
      Icon: FaUserShield,
      gradient: "action-support",
    }
  ];

  return (
    <div className="sa-home">
      <header className="sa-header">
        <div className="sa-header-content">
          <h1 className="sa-header-title">Admin Dashboard Overview</h1>
          <p className="sa-header-description">
            Manage organizations, users, and transactions from this centralized admin dashboard.
            Monitor system activity and handle administrative tasks efficiently.
          </p>
        </div>
      </header>

      {/* Statistics Section */}
      <section className="sa-stats-section">
        <div className="sa-stats-grid">
          {statCards.map((stat, idx) => {
            const IconComponent = stat.icon;
            return (
              <Link key={idx} to={stat.link} className="sa-stat-card-link">
                <div className={`sa-stat-card ${stat.gradient}`}>
                  <div className="sa-stat-icon-wrapper">
                    <IconComponent className="sa-stat-icon" />
                  </div>
                  <div className="sa-stat-content">
                    <h3 className="sa-stat-title">{stat.title}</h3>
                    <p className="sa-stat-count">
                      {stats.loading ? (
                        <span className="sa-stat-loading">...</span>
                      ) : (
                        stat.count
                      )}
                    </p>
                  </div>
                  <div className="sa-stat-overlay"></div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Quick Actions Section */}
      <section className="sa-quick-actions-section">
        <h2 className="sa-section-title">Quick Actions</h2>
        <div className="sa-quick-actions-grid">
          {quickActions.map((action, idx) => {
            const IconComponent = action.Icon;
            return (
              <Link key={idx} to={action.link} className="sa-action-card-link">
                <div className={`sa-action-card ${action.gradient}`}>
                  <div className="sa-action-icon-wrapper">
                    <IconComponent className="sa-action-icon" />
                  </div>
                  <div className="sa-action-content">
                    <h3 className="sa-action-title">{action.title}</h3>
                    <p className="sa-action-description">{action.description}</p>
                  </div>
                  <div className="sa-action-arrow">→</div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}

